/**
 * Garmin-Dateien lesen — Normalisierung und Hash, ohne Oberfläche.
 *
 * Der content_hash muss zeichengleich zu dem der Pipeline sein. Zwei Stellen
 * entscheiden darüber, und beide sind Fallen: die Rundung und die Zeit.
 */

/**
 * Runden wie Pythons `round()`: kaufmännisch, aber `.5` zur **geraden** Zahl.
 *
 * `Math.round()` rundet `.5` immer auf und ergäbe für jeden Tauchgang, dessen
 * Dauer oder Tiefe genau dazwischen liegt, einen anderen Hash als die Pipeline.
 * Im Bestand betrifft das 16 Tauchgänge.
 */
export function runde(wert) {
  const abgerundet = Math.floor(wert);
  const rest = wert - abgerundet;
  if (rest > 0.5) return abgerundet + 1;
  if (rest < 0.5) return abgerundet;
  // Genau auf der Kippe: zur geraden Zahl.
  return abgerundet % 2 === 0 ? abgerundet : abgerundet + 1;
}

/**
 * Garmins Zeitstempel zerlegen: `"2022-12-28T07:30:00+05:00"`.
 *
 * Für den Hash zählen die Ziffern **vor** dem Offset — das sind 07:30 Ortszeit.
 * Wer nach UTC umrechnet, erhält 02:30 und damit für jeden Tauchgang ausserhalb
 * der UTC-Zone einen anderen Hash als die Pipeline.
 *
 * Das Feld `timezone` der Quelle taugt nicht: Garmin trägt dort irgendeine Zone
 * mit passendem Offset ein, die Malediven etwa als "Asia/Aqtobe". Verlässlich
 * ist nur der Offset am Zeitstempel.
 */
export function zerlegeStartzeit(wert) {
  if (typeof wert !== 'string' || !wert.trim()) return null;
  const treffer = wert.trim().match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/,
  );
  if (!treffer) return null;

  const [, datum, stunde, minute, sekunde, zone] = treffer;
  // Der Hash rechnet auf die Minute, `start_utc` behaelt die Sekunden — sonst
  // stuende in der App ein anderer Zeitpunkt als in der Pipeline.
  const lokal = `${datum}T${stunde}:${minute}`;
  const genau = `${lokal}:${sekunde ?? '00'}`;

  let versatz = null;
  if (zone === 'Z') {
    versatz = 0;
  } else if (zone) {
    const zeichen = zone[0] === '-' ? -1 : 1;
    const [zs, zm] = zone.slice(1).replace(':', '').match(/.{2}/g).map(Number);
    versatz = zeichen * (zs * 60 + zm);
  }

  const zeitpunkt = versatz === null
    ? new Date(`${genau}Z`)
    : new Date(Date.parse(`${genau}Z`) - versatz * 60000);
  return { lokal, versatz, utc: alsPipelineZeit(zeitpunkt) };
}

/**
 * `2018-09-25T00:10:46+00:00` — so schreibt die Pipeline.
 *
 * `toISOString()` liefert `…T00:10:46.000Z`: derselbe Zeitpunkt, eine andere
 * Zeichenkette. Zwei Schreibweisen fuer denselben Satz sind eine Fehlerquelle
 * bei jedem Vergleich, der nicht ueber Date geht.
 */
function alsPipelineZeit(zeitpunkt) {
  return `${zeitpunkt.toISOString().slice(0, 19)}+00:00`;
}

/**
 * Der Dedup-Schlüssel, zeichengleich zur Pipeline (CLAUDE.md, B).
 *
 * `sha256("YYYY-MM-DDTHH:MM|dauer_min|tiefe_dm")` — Dauer in ganzen Minuten,
 * Tiefe in ganzen Dezimetern, beides mit `runde()` und nicht mit Math.round.
 */
export async function inhaltsHash(lokal, dauerSekunden, tiefeM) {
  const dauerMin = runde(dauerSekunden / 60);
  const tiefeDm = tiefeM === null || tiefeM === undefined ? '?' : runde(tiefeM * 10);
  const roh = `${lokal}|${dauerMin}|${tiefeDm}`;
  const puffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(roh));
  return [...new Uint8Array(puffer)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const zahl = (wert) => (typeof wert === 'number' && Number.isFinite(wert) ? wert : null);
const text = (wert) => (typeof wert === 'string' && wert.trim() ? wert.trim() : null);
const klein = (wert) => text(wert)?.toLowerCase() ?? null;

// Sieht aus wie "24.9012, 35.8891"? Dann ist es kein Platzname.
const KOORDINATENTEXT = /^-?\d+[.,]\d+\s*[,;]\s*-?\d+[.,]\d+$/;

/**
 * Ist das eine Tauchgangsdatei?
 *
 * Geprüft wird der Inhalt, nicht der Dateiname: Der Export enthält zwölf
 * `Dive-GEAR*.json`, und ein umbenanntes Verzeichnis darf keine Ausnahme sein.
 */
export function istTauchgang(inhalt) {
  const daten = inhalt?.data;
  if (!daten || typeof daten !== 'object') return false;
  return typeof daten.startTime === 'string' || typeof daten.number === 'number';
}

/**
 * Der Tauchplatz. `location.location` ist der Platz, `name` der Titel.
 *
 * Bei neun Dives steht im Ortsfeld eine Koordinatenzeichenkette, bei 189 fehlt
 * es ganz — dann trägt der Titel den Ort ("Galapagos Corales (Wolf Island)").
 */
function platzname(d) {
  const ort = text(d.location?.location);
  if (ort && !KOORDINATENTEXT.test(ort)) return ort;
  return text(d.name);
}

/**
 * Grundzeit schlägt Gesamtzeit.
 *
 * `bottomTime` ist in allen 358 Dives gesetzt, `totalTime` fehlt bei den
 * manuell erfassten und ist vereinzelt kaputt. Diving Log misst dieselbe
 * Grösse — das hält den content_hash über beide Quellen stabil.
 */
function dauerSekunden(d) {
  const grundzeit = zahl(d.profile?.bottomTime);
  if (grundzeit !== null) return Math.round(grundzeit);
  const gesamt = zahl(d.totalTime);
  return gesamt === null ? null : Math.round(gesamt);
}

/**
 * Die Flaschen aus `equipment.gases`.
 *
 * Werte mit fremder Einheit werden verworfen, nicht umgerechnet — ein falsch
 * gedeuteter Druck ist schlimmer als ein fehlender.
 */
function flaschen(d) {
  return (d.equipment?.gases ?? []).filter((g) => g && typeof g === 'object').map((gas) => ({
    source_id: gas.gasId != null ? String(gas.gasId) : null,
    label: klein(gas.gasStatus),
    gas_type: klein(gas.gasType),
    gas_mode: klein(gas.gasMode),
    o2_pct: zahl(gas.percentOxygen),
    he_pct: zahl(gas.percentHelium),
    volume_l: !gas.tankSizeUnit || klein(gas.tankSizeUnit) === 'liter'
      ? zahl(gas.tankSize) : null,
    start_bar: !gas.startPressureUnit || klein(gas.startPressureUnit) === 'bar'
      ? zahl(gas.startPressure) : null,
    end_bar: !gas.endPressureUnit || klein(gas.endPressureUnit) === 'bar'
      ? zahl(gas.endPressure) : null,
    tank_type: klein(gas.tankType),
  }));
}

const ANZUG = { nass: 'wet', wet: 'wet', trocken: 'dry', dry: 'dry',
  shorty: 'shorty', haut: 'skin', skin: 'skin', semidry: 'semidry' };

/**
 * Eine Garmin-Datei zu einem Tauchgang.
 *
 * Die Feldpfade stammen aus `src/tauchlogbuch/importers/garmin_json.py` — dort
 * sind sie an 358 echten Dateien erprobt. Übernommen werden die Kernfelder;
 * CNS, N2, O2-Toxizität, Kalorien und die Deko-Einstellungen bleiben aussen
 * vor und kommen nach, sobald dieselbe Datei über die Pipeline läuft.
 */
export async function normalisiere(inhalt) {
  if (!istTauchgang(inhalt)) return null;
  const d = inhalt.data;
  const hinweise = [];

  const zeit = zerlegeStartzeit(d.startTime);
  if (!zeit) hinweise.push('ohne verwertbare Startzeit');

  const dauer = dauerSekunden(d);
  if (dauer === null) hinweise.push('ohne Dauer — kein Hash bildbar');

  const umgebung = d.environment ?? {};
  const ausruestung = d.equipment ?? {};
  const leistung = d.performance ?? {};
  const einstieg = d.location?.entryLoc ?? {};
  const ausstieg = d.location?.exitLoc ?? {};
  const tiefe = zahl(d.profile?.maxDepth);

  // Einheiten werden geprüft, nicht umgerechnet — ein falsch gedeuteter Wert
  // wäre schlimmer als ein fehlender.
  const sicht = !umgebung.visibilityUnit || klein(umgebung.visibilityUnit) === 'meter'
    ? zahl(umgebung.visibility) : null;
  const blei = !ausruestung.weightUnit || klein(ausruestung.weightUnit) === 'kilogram'
    ? zahl(ausruestung.weight) : null;

  const tauchgang = {
    source: 'garmin_json',
    source_id: d.connectActivityId != null ? String(d.connectActivityId) : null,
    raw_ref: `garmin_json#${d.connectActivityId ?? d.id ?? '?'}`,
    dive_number: zahl(d.number),
    activity_title: text(d.name),
    start_utc: zeit?.utc ?? null,
    tz_offset_min: zeit?.versatz ?? null,
    timezone: text(d.timezone),
    duration_s: dauer,
    surface_interval_s: zahl(d.profile?.surfaceInterval),
    max_depth_m: tiefe,
    avg_depth_m: zahl(d.profile?.averageDepth),
    site_name: platzname(d),
    entry_lat: zahl(einstieg.latitude),
    entry_lon: zahl(einstieg.longitude),
    exit_lat: zahl(ausstieg.latitude),
    exit_lon: zahl(ausstieg.longitude),
    water_temp_avg_c: zahl(umgebung.avgTemperature),
    water_temp_min_c: zahl(umgebung.minTemperature),
    water_temp_max_c: zahl(umgebung.maxTemperature),
    visibility_m: sicht,
    water_type: klein(umgebung.waterType),
    entry_type: klein(umgebung.entryType),
    current: klein(umgebung.waterCurrent),
    surface_cond: klein(umgebung.surfaceCondition),
    weight_kg: blei,
    suit_type: ANZUG[klein(ausruestung.suitTypeLegacy)] ?? null,
    suit_thickness_mm: zahl(ausruestung.suitThickness),
    hr_avg_bpm: zahl(leistung.avgHr),
    hr_max_bpm: zahl(leistung.maxHr),
    hr_min_bpm: zahl(leistung.minHr),
    rmv_l_min: zahl(d.avgRMV),
    vsac_l_min: zahl(d.avgVSAC),
    psac_bar_min: zahl(d.avgPSAC),
    notes: text(d.notes),
    buddy_raw: text(d.buddy),
    buddies: text(d.buddy) ? [text(d.buddy)] : [],
    has_profile: false,   // Profile kommen aus den FIT-Dateien
    tanks: flaschen(d),
    content_hash: null,
  };

  if (zeit && dauer !== null) {
    tauchgang.content_hash = await inhaltsHash(zeit.lokal, dauer, tiefe);
  }
  return { tauchgang, hinweise };
}
